import React from 'react'
import {createRoot} from 'react-dom/client'
import {Provider} from 'react-redux'
import {registerApiDispatch} from '~/api/client/dispatch'
import SinglePageApplication from '~/pages/SPA'
import {initSettingsSync} from '~/redux/reducers/settings'
import store from '~/redux/store'
import {applyDarkModeToDocument, prefersDarkMode} from '~/utils/darkMode'
import {LocalStorageManager} from '~/utils/LocalStorageManager'
import './global.global.css'

const target = document.querySelector('#root')

/**
 * A startup snapshot of `localStorage`, kept unexported because it goes stale as soon as the
 * setting changes — read `darkMode` from redux instead. Duplicates the settings reducer's read
 * because the store doesn't exist yet; both fall back to `prefersDarkMode` so they agree.
 */
const initialDarkMode = (): boolean => {
  const stored = LocalStorageManager.get('settings:darkMode')
  return stored.type === 'success' ? stored.result === 'true' : prefersDarkMode()
}

// paint before the first render, so there's no flash of the wrong theme
let appliedDarkMode = initialDarkMode()
applyDarkModeToDocument(appliedDarkMode)

// A subscription rather than a call per dispatcher: `setDarkMode`, `resetSettings` and
// `_ROOT_LOGOUT` all recompute `darkMode`. This keeps the DOM in sync with N edit paths
// while also keeping the DOM dependency  out of those paths' module graphs.
store.subscribe(() => {
  const {darkMode} = store.getState().settingsState
  if (darkMode !== appliedDarkMode) {
    appliedDarkMode = darkMode
    applyDarkModeToDocument(darkMode)
  }
})

// Wired up here because this is the composition root — the only place that sees both the store
// and the DOM, which is what keeps the API and routing layers free of the store.
registerApiDispatch(store.dispatch)

// The teardown is dropped on purpose: this module exports nothing, so Fast Refresh can't hot-swap
// it and a save forces a full reload. If it ever exports a component, the listener would stack on
// each reload — hook the teardown to `import.meta.hot?.dispose(...)` then.
initSettingsSync(store.dispatch)

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(target!).render(
  <React.StrictMode>
    <Provider store={store}>
      <SinglePageApplication />
    </Provider>
  </React.StrictMode>
)
